/**
 * useNodeSelection - Manages node selection state and delete handling
 *
 * Why custom delete handling instead of React Flow's built-in:
 * - React Flow's default deleteKeyCode captures Backspace globally
 * - This interferes with text editing in node inputs (users can't backspace)
 * - We check document.activeElement to avoid deleting during text input
 *
 * Usage:
 * ```tsx
 * const { selectedNodeIds, onSelectionChange } = useNodeSelection();
 *
 * // Wire to React Flow
 * <ReactFlow onSelectionChange={onSelectionChange} />
 *
 * // Wire delete shortcut with custom handler
 * useDeleteShortcut(handleDelete, selectedNodeIds);
 * ```
 */

import { useCallback, useEffect, useState } from 'react';
import { type Node } from '@xyflow/react';

/**
 * Hook to track which nodes are currently selected.
 *
 * @returns Object with:
 * - selectedNodeIds: Array of selected node IDs
 * - onSelectionChange: Callback to wire to React Flow's onSelectionChange prop
 */
export function useNodeSelection() {
  // Track selected node IDs in local state
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);

  /**
   * Callback for React Flow's onSelectionChange event.
   * Extracts node IDs from the selection and updates state.
   */
  const onSelectionChange = useCallback(({ nodes }: { nodes: Node[] }) => {
    setSelectedNodeIds(nodes.map(n => n.id));
  }, []);

  return {
    selectedNodeIds,
    onSelectionChange,
  };
}

/**
 * Hook to handle Delete/Backspace key for removing selected nodes.
 *
 * CRITICAL: This hook checks if user is typing before triggering delete.
 * Without this check, pressing Backspace in a text input would delete nodes!
 *
 * @param onDelete - Callback when delete is triggered, receives array of selected node IDs
 * @param selectedIds - Currently selected node IDs (pass from useNodeSelection)
 *
 * @example
 * ```tsx
 * const { selectedNodeIds } = useNodeSelection();
 *
 * const handleDelete = useCallback((ids: string[]) => {
 *   makeChange(() => {
 *     ids.forEach(id => nodesMap.delete(id));
 *   });
 * }, [nodesMap, makeChange]);
 *
 * useDeleteShortcut(handleDelete, selectedNodeIds);
 * ```
 */
export function useDeleteShortcut(
  onDelete: (ids: string[]) => void,
  selectedIds: string[]
) {
  useEffect(() => {
    /**
     * Handle keydown events for Delete and Backspace.
     *
     * Steps:
     * 1. Check if user is typing (input, textarea, contenteditable)
     * 2. If typing, do nothing (let browser handle key normally)
     * 3. If not typing and nodes are selected, trigger delete
     */
    const handleKeyDown = (e: KeyboardEvent) => {
      // -----------------------------------------------------------------------
      // Check if user is actively typing in a text input element.
      // If so, we should NOT delete nodes - let the browser handle the key.
      // -----------------------------------------------------------------------
      const activeElement = document.activeElement;

      // Check for standard form inputs
      const isInput = activeElement?.tagName === 'INPUT';
      const isTextarea = activeElement?.tagName === 'TEXTAREA';

      // Check for contenteditable elements (like Tiptap editors)
      const isContentEditable =
        activeElement?.getAttribute('contenteditable') === 'true';

      // If user is typing in any of these, don't intercept the key
      if (isInput || isTextarea || isContentEditable) {
        return;
      }

      // -----------------------------------------------------------------------
      // Handle Delete or Backspace keys
      // -----------------------------------------------------------------------
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        // Prevent default browser behavior (e.g., navigating back on Backspace)
        e.preventDefault();

        // Trigger the delete callback with all selected node IDs
        onDelete(selectedIds);
      }
    };

    // Add event listener to document (captures all keydown events)
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup: remove listener when component unmounts or dependencies change
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onDelete, selectedIds]);
}
